import axios, { AxiosResponse, isAxiosError } from "axios";
import { User, UserFuncao } from "../../context";
import { Paginated, RequestError } from "./api";

const getUser = async (): Promise<User | RequestError> => {
  try {
    const data = await axios.get(`${process.env.REACT_APP_URL}/user/data`);
    return processUser(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao obter os dados do usuário",
      };
    } else {
      throw error;
    }
  }
};

const getUserById = async (id: string): Promise<User | RequestError> => {
  try {
    const data = await axios.get(`${process.env.REACT_APP_URL}/user/${id}`);
    return processUser(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao obter os dados do usuário",
      };
    } else {
      throw error;
    }
  }
};

interface AllUsers {
  items: string[]; // Id dos usuários
  page: number;
  perPage: number;
  totalPages: number;
  totalItems: number;
}
const getAllInMyCompany = async (
  page: number,
  perPage: number
): Promise<AllUsers | RequestError> => {
  try {
    const data: AxiosResponse<AllUsers> = await axios.get(
      `${process.env.REACT_APP_URL}/user/list_my_users/${page.toFixed(
        0
      )}/${perPage.toFixed(0)}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao obter os dados do usuário",
      };
    } else {
      throw error;
    }
  }
};

const getByNameInMyCompany = async (
  name: string,
  page: number,
  perPage: number
): Promise<AllUsers | RequestError> => {
  try {
    const data: AxiosResponse<AllUsers> = await axios.get(
      `${
        process.env.REACT_APP_URL
      }/user/my_users_by_name/${name}/${page.toFixed(0)}/${perPage.toFixed(0)}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao obter os dados do usuário",
      };
    } else {
      throw error;
    }
  }
};

const getAll = async (
  page: number,
  perPage: number
): Promise<AllUsers | RequestError> => {
  try {
    const data: AxiosResponse<AllUsers> = await axios.get(
      `${process.env.REACT_APP_URL}/user/list_users/${page.toFixed(
        0
      )}/${perPage.toFixed(0)}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao obter os dados do usuário",
      };
    } else {
      throw error;
    }
  }
};

const getByName = async (
  name: string,
  page: number,
  perPage: number
): Promise<AllUsers | RequestError> => {
  try {
    const data: AxiosResponse<AllUsers> = await axios.get(
      `${process.env.REACT_APP_URL}/user/users_by_name/${name}/${page.toFixed(
        0
      )}/${perPage.toFixed(0)}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao obter os dados do usuário",
      };
    } else {
      throw error;
    }
  }
};

export interface CreateUserPayload {
  nome: string;
  email: string;
  telefones?: string;
  sendPassword?: boolean;
  admin: boolean;
  funcoes: UserFuncao[];
  empreedimento: string;
}
async function create(data: CreateUserPayload): Promise<string | RequestError> {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_URL}/user/add_user`,
      data
    );
    return res.data.user;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Erro",
        message:
          error.response?.data.message ?? "Erro ao cadastrar funcionário",
      };
    }
    throw error;
  }
}

// Funções de Login

const login = async (
  email: string,
  password: string
): Promise<{ token: string; } | RequestError> => {
  try {
    const data: AxiosResponse<{ token: string; }> = await axios.post(
      `${process.env.REACT_APP_URL}/user/login`,
      {
        email: email,
        password: password,
      }
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Login Error",
        message: error.response?.data.message ?? "Erro ao relaizar o login",
      };
    } else {
      throw error;
    }
  }
};

const logout = async (): Promise<{ ok: boolean; } | RequestError> => {
  try {
    const data: AxiosResponse<{ ok: boolean; }> = await axios.get(
      `${process.env.REACT_APP_URL}/user/logout`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Login Error",
        message: error.response?.data.message ?? "Erro ao relaizar o login",
      };
    } else {
      throw error;
    }
  }
};

// Passsword
const changePassw = async (
  oldPass: string,
  newPass: string
): Promise<{ ok: boolean; } | RequestError> => {
  try {
    const data: AxiosResponse<{ ok: boolean; }> = await axios.post(
      `${process.env.REACT_APP_URL}/user/change_password`,
      {
        oldPass: oldPass,
        newPass: newPass,
      }
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ?? "Erro ao alterar a senha do usuário",
      };
    } else {
      throw error;
    }
  }
};

// Funcionários
const getFuncionarios = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<User> | RequestError> => {
  try {
    const res = await axios.get<Paginated<User>>(
      `${process.env.REACT_APP_URL}/user/list_my_users/${idBarragem}/${page}/${perPage}`
    );
    return {
      ...res.data,
      items: res.data.items.map(processUser),
    };
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Erro",
        message: error.response?.data.message ?? "Erro ao buscar funcionários",
      };
    }
    throw error;
  }
};

const getFuncionariosByCompany = async (
  idEmpreendimento: string,
  page: number,
  perPage: number
): Promise<Paginated<User> | RequestError> => {
  try {
    const res = await axios.get<Paginated<User>>(
      `${process.env.REACT_APP_URL}/user/users_by_company/${idEmpreendimento}/${page}/${perPage}`
    );
    return {
      ...res.data,
      items: res.data.items.map(processUser),
    };
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Erro",
        message: error.response?.data.message ?? "Erro ao buscar funcionários",
      };
    }
    throw error;
  }
};

async function remove(id: string): Promise<true | RequestError> {
  try {
    await axios.delete(`${process.env.REACT_APP_URL}/user/delete_user/${id}`);
    return true;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Erro",
        message: error.response?.data.message ?? "Erro ao excluir funcionário",
      };
    }
    throw error;
  }
}

async function update(
  id: string,
  data: Partial<User>
): Promise<User | RequestError> {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_URL}/user/update_user/${id}`,
      data
    );
    return processUser(res.data.user);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: "Erro ao atualizar usuário",
        message: error?.response?.data?.message || error.message,
      };
    }
    throw error;
  }
}

const sendPassword = async (
  id: string
): Promise<{ email: string; } | RequestError> => {
  try {
    const data = await axios.get(
      `${process.env.REACT_APP_URL}/user/send_password/${id}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "User Error",
        message:
          error.response?.data.message ??
          "Erro ao enviar a senha para o usuário",
      };
    } else {
      throw error;
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function processUser(user: any): User {
  if (user.empreedimento) user.empreendimento = user.empreedimento;
  if (user.insertDate) user.insertDate = new Date(user.insertDate);
  if (user.updateDate) user.updateDate = new Date(user.updateDate);
  return user;
}

export const ApiUser = {
  getAllInMyCompany,
  getByNameInMyCompany,
  getUser,
  getByName,
  getUserById,
  getAll,
  login,
  logout,
  changePassw,
  getFuncionarios,
  getFuncionariosByCompany,
  remove,
  update,
  create,
  sendPassword,
};
